import { AutocompleteWithLabel } from '@adotel/components'
import { useLazyQuery, useQuery } from '@apollo/client'
import { useEffect } from 'react'

import { ACCOMMODATIONS_QUERY } from '../../graphql/accommodation'
import { PROVIDERS_QUERY } from '../../graphql/providerQueries'
import { getTranslate } from '../../localization'
import { Accommodation } from '../../types/accommodation'
import { ProviderBrief } from '../../types/provider'
import { RateConnectionListFilters } from '../../types/rateConnection'
import useStyles from './styles/PriceClassDistributionsBasicFilters.styles'

type PriceClassDistributionsBasicFiltersProps = {
  autoCompleteForProvider: any
  setAutoCompleteForProvider: React.Dispatch<any>
  autoCompleteForAccommodation: any
  setAutoCompleteForAccommodation: React.Dispatch<any>
  inputFilters: RateConnectionListFilters
  setInputFilters: React.Dispatch<
    React.SetStateAction<RateConnectionListFilters>
  >
  isRoleProvider: boolean
}

export default function PriceClassDistributionsBasicFilters({
  autoCompleteForProvider,
  setAutoCompleteForProvider,
  autoCompleteForAccommodation,
  setAutoCompleteForAccommodation,
  inputFilters,
  setInputFilters,
  isRoleProvider
}: PriceClassDistributionsBasicFiltersProps) {
  const classes = useStyles()

  const { data: providerData, loading: loadingForProvider } = useQuery<{
    providers: ProviderBrief[]
  }>(PROVIDERS_QUERY, {
    notifyOnNetworkStatusChange: true
  })

  const [
    getAccommodations,
    { data: accommodationsData, loading: loadingForAccommodation }
  ] = useLazyQuery<{
    accommodations: Accommodation[]
  }>(ACCOMMODATIONS_QUERY, { notifyOnNetworkStatusChange: true })

  useEffect(() => {
    if (autoCompleteForProvider) {
      getAccommodations({
        variables: {
          providerCode: autoCompleteForProvider.code
        }
      })

      setInputFilters({
        ...inputFilters,
        providerCode: autoCompleteForProvider
          ? autoCompleteForProvider.code
          : ''
      })
    }
  }, [autoCompleteForProvider])

  useEffect(() => {
    if (autoCompleteForAccommodation) {
      setInputFilters({
        ...inputFilters,
        propertyCode: autoCompleteForAccommodation
          ? autoCompleteForAccommodation.code
          : ''
      })
    }
  }, [autoCompleteForAccommodation])

  return (
    <div className={classes.root}>
      <div>
        <AutocompleteWithLabel
          inputSize="small"
          noHelperText
          fullWidth
          value={autoCompleteForProvider}
          label={getTranslate('Provider')}
          placeholder={getTranslate('Select provider')}
          options={
            providerData?.providers
              ? providerData?.providers.map((p) => ({
                  label: `${p.name} - (${p.displayProviderCode})`,
                  value: p.id,
                  code: p.providerCode
                }))
              : []
          }
          onChange={(_, v: any) => {
            if (v) {
              setAutoCompleteForProvider({
                value: v.value,
                label: v.label,
                code: v.code
              })
            } else {
              setAutoCompleteForProvider(null)
              setAutoCompleteForAccommodation(null)
            }
          }}
          disabled={isRoleProvider}
          getOptionLabel={(option) => option.label || ''}
          getOptionSelected={(option, value) => option.value === value.value}
          loading={loadingForProvider}
          loadingText={getTranslate('Loading') + '...'}
        />
      </div>
      <div>
        <AutocompleteWithLabel
          value={autoCompleteForAccommodation}
          label={getTranslate('Accommodation')}
          placeholder={getTranslate('Select accommodation')}
          onChange={(_, v: any) => {
            if (v) {
              setAutoCompleteForAccommodation({
                value: v.value,
                label: v.label,
                code: v.code
              })
            } else {
              setAutoCompleteForAccommodation(null)
            }
          }}
          limitTags={3}
          getOptionSelected={(option, value) => option.value === value.value}
          getOptionLabel={(option) => option.label || ''}
          options={
            accommodationsData?.accommodations && autoCompleteForProvider
              ? accommodationsData.accommodations.map((acc) => ({
                  label: acc.basicDetails.name,
                  value: acc.basicDetails.accommodationID || '',
                  code: acc.basicDetails.propertyCode
                }))
              : []
          }
          size="small"
          inputSize="small"
          loading={loadingForAccommodation}
          loadingText={getTranslate('Loading') + '...'}
        />
      </div>
    </div>
  )
}
